import { MUI_AUTOCOMPLETE_POPPER_SELECTOR } from './case-plan-create.constants';
import { isTemplatePlanType } from './case-plan-create-payload.util';

/**
 * Returns the first non-empty string from the given values.
 * @param {...*} values
 * @returns {string}
 */
const firstNonEmpty = (...values) => {
  const match = values.find((value) => Boolean(value));
  return match ? String(match) : '';
};

/**
 * Resolves a resident's display name from name / first+last fields.
 * @param {object} resident
 * @returns {string}
 */
export const getResidentDisplayName = (resident) => {
  if (!resident) {
    return '';
  }

  if (resident.name) {
    return resident.name;
  }

  return `${resident.firstName ?? ''} ${resident.lastName ?? ''}`.trim();
};

/**
 * Builds two-letter initials from a resident display name.
 * @param {string} name
 * @returns {string}
 */
export const getResidentInitials = (name) => {
  const parts = String(name ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 0) {
    return '';
  }

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0][0]}${parts.at(-1)[0]}`.toUpperCase();
};

/**
 * Formats the secondary meta line for a selected resident card.
 * @param {object} resident
 * @returns {string}
 */
export const formatResidentMetaLine = (resident) => {
  if (!resident) {
    return '';
  }

  const username = resident.username ?? 'username';
  const docId = resident.docId ? `DOC ID: ${resident.docId}` : 'DOC ID: ########';
  const location = resident.location ?? resident.facilityName ?? 'Location';

  return `${username} | ${docId} | ${location}`;
};

/**
 * Formats a resident option label for autocomplete display.
 * @param {object} resident
 * @returns {string}
 */
export const formatResidentLabel = (resident) => {
  if (!resident) {
    return '';
  }

  const fullName = getResidentDisplayName(resident);
  if (fullName && resident.username) {
    return `${fullName} (${resident.username})`;
  }

  return firstNonEmpty(fullName, resident.username, resident.docId);
};

/**
 * Formats an editor option label for autocomplete display.
 * @param {object} editor
 * @returns {string}
 */
export const formatEditorLabel = (editor) => {
  if (!editor) {
    return '';
  }

  if (editor.name && editor.username) {
    return `${editor.name} (${editor.username})`;
  }

  return firstNonEmpty(editor.name, editor.username);
};

/**
 * True when the click target lives inside the Autocomplete list (portaled to body).
 * @param {EventTarget|null} target
 * @returns {boolean}
 */
export const isAutocompletePopperClick = (target) => {
  if (!(target instanceof Element)) {
    return false;
  }

  return Boolean(target.closest(MUI_AUTOCOMPLETE_POPPER_SELECTOR));
};

/**
 * Builds the default editors list seeded with the current user as creator.
 * @param {{ personId?: number|string, name?: string, username?: string }|null} user
 * @returns {object[]}
 */
export const buildDefaultEditorsFromUser = (user) => {
  if (!user) {
    return [];
  }

  return [
    {
      personId: user.personId,
      name: user.name,
      username: user.username,
      isCreator: true,
    },
  ];
};

/**
 * Builds the "N editors on this plan" supporting copy.
 * @param {number} editorCount
 * @returns {string}
 */
export const buildEditorsHelperText = (editorCount) => {
  const noun = editorCount === 1 ? 'editor' : 'editors';
  return `${editorCount} ${noun} on this plan - Creator is always added by default`;
};

/**
 * Copy for the Plan Type switcher prompt + action label.
 * @param {boolean} isTemplateMode
 * @returns {{ prompt: string, actionLabel: string }}
 */
export const getPlanTypeSwitcherCopyForMode = (isTemplateMode) => {
  if (isTemplateMode) {
    return {
      prompt: 'Building a plan for a specific resident?',
      actionLabel: 'Switch to a resident plan',
    };
  }

  return {
    prompt: 'Need to create a template instead?',
    actionLabel: 'Create a template',
  };
};

/**
 * Copy for the Plan Type switcher prompt + action label.
 * @param {object|null} planType
 * @returns {{ prompt: string, actionLabel: string }}
 */
export const getPlanTypeSwitcherCopy = (planType) =>
  getPlanTypeSwitcherCopyForMode(isTemplatePlanType(planType));
